import { skipToken } from "@reduxjs/toolkit/query";
import {
  type Automation,
  automationIdSchema,
  AutomationTemplateIds,
  getAutomationPlaybookIds,
} from "@repo/app-types/entities/automation";
import { renderTeammateDisplayName } from "@repo/app-types/entities/teammate";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { type FC, useState } from "react";
import {
  Navigate,
  Outlet,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router";
import {
  useAutomationByIdQuery,
  useDeleteAutomationMutation,
  useUpdateAutomationMutation,
} from "../../api/entities/automation.js";
import { useCreatePreviewAutomationRunMutation } from "../../api/entities/automationRun.js";
import { useBasicTeammateQuery } from "../../api/entities/teammate.js";
import { appPaths } from "../../app/appPaths.js";
import {
  PageBody,
  PageLayout,
  PageLayoutHeight,
} from "../../components/layout/pageLayout.js";
import { RouteTabs } from "../../components/layout/routeTabs.js";
import { AutomationDetailActions } from "../../features/automations/automationDetailActions.js";
import { AutomationTemplateDescription } from "../../features/automations/automationTemplateDescription.js";
import { DeleteAutomationModal } from "../../features/automations/deleteAutomationModal.js";
import { useAutomationFieldsContext } from "../../features/automations/useAutomationFieldsContext.js";
import { useAiCreditsDisabledReason } from "../../features/billing/aiCreditsDisabledTooltip.js";
import { useIsAiCreditsExhausted } from "../../features/billing/aiCreditsExhaustedIndicator.js";
import { useUnsavedChangesGuard } from "../../hooks/useUnsavedChangesGuard.js";

export interface AutomationDetailOutletContext {
  automation: Automation;
  automationsHref: string;
  reportHasUnsavedChanges: (hasUnsavedChanges: boolean) => void;
}

export function useAutomationDetailOutletContext(): AutomationDetailOutletContext {
  return useOutletContext<AutomationDetailOutletContext>();
}

/**
 * Layout for a saved automation: breadcrumb, header (delete / run preview /
 * enable), and the Overview | Results | Runs tabs rendered via the outlet.
 */
export const AutomationDetailPage: FC = () => {
  const { playbookId, automationId } = useParams();

  const parsedAutomationId = automationId
    ? automationIdSchema.safeParse(automationId)
    : undefined;

  const { data: automation, isLoading } = useAutomationByIdQuery(
    parsedAutomationId?.success ? parsedAutomationId.data : skipToken,
  );

  if (!parsedAutomationId?.success) {
    return <Navigate to=".." replace />;
  }

  if (isLoading) {
    return (
      <div className="flex min-h-full items-center justify-center">
        <Loader style={LoaderStyle.ZOOMIES} />
      </div>
    );
  }

  if (!automation) {
    return <Navigate to=".." replace />;
  }

  return (
    <AutomationDetailPageContent
      key={automation.id}
      automation={automation}
      automationsHref={
        playbookId ? appPaths.playbookAutomations.build({ playbookId }) : ".."
      }
    />
  );
};

const AutomationDetailPageContent: FC<{
  automation: Automation;
  automationsHref: string;
}> = ({ automation, automationsHref }) => {
  const navigate = useNavigate();

  // The coaching template is a thin configuration shell over the existing
  // playbook coaching feature: it produces no automation runs, so we hide the
  // Overview/Results/Runs tab strip (showing the config directly) and the
  // preview run.
  const isCoachingTemplate =
    automation.template_id === AutomationTemplateIds.TEAMMATE_COACHING;

  const [updateAutomation] = useUpdateAutomationMutation();
  const [deleteAutomation] = useDeleteAutomationMutation();
  const [createPreviewRun, { isLoading: isPreviewRunStarting }] =
    useCreatePreviewAutomationRunMutation();
  const isCreditsExhausted = useIsAiCreditsExhausted();
  const aiCreditsDisabledReason = useAiCreditsDisabledReason();
  const { playbookNamesById, isReadOnly } = useAutomationFieldsContext();

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [hasUnsavedConfig, setHasUnsavedConfig] = useState(false);
  // Held here rather than in the Overview tab that reports the edits, because
  // this page also owns Delete, whose redirect must not stop to ask about them.
  const { guardModal, navigateWithoutConfirmation } =
    useUnsavedChangesGuard(hasUnsavedConfig);

  const playbookName =
    getAutomationPlaybookIds(automation.settings)
      .map((playbookId) => playbookNamesById.get(playbookId))
      .filter(Boolean)
      .join(", ") || undefined;

  const { data: creator } = useBasicTeammateQuery(
    automation.created_by_teammate_id ?? skipToken,
  );
  const createdByName = creator
    ? renderTeammateDisplayName(creator)
    : undefined;

  const onToggleEnabled = async (isEnabled: boolean) => {
    await updateAutomation({
      id: automation.id,
      is_enabled: isEnabled,
    });
  };

  const onRunPreview = async () => {
    try {
      const run = await createPreviewRun(automation.id).unwrap();
      navigate(`runs/${encodeURIComponent(run.id)}`);
    } catch {
      // Starting the preview run failed; keep the user on the detail page.
    }
  };

  const onDelete = async () => {
    try {
      await deleteAutomation(automation.id).unwrap();
      navigateWithoutConfirmation(() => {
        navigate(automationsHref);
      });
    } catch {
      // Deletion failed; keep the user on the detail page.
    }
  };

  return (
    <PageLayout height={PageLayoutHeight.Fills}>
      {guardModal}
      <PageHeader
        eyebrow={playbookName}
        title={automation.name}
        actions={
          <AutomationDetailActions
            automation={automation}
            createdByName={createdByName}
            isReadOnly={isReadOnly}
            onToggleEnabled={(isEnabled) => {
              void onToggleEnabled(isEnabled);
            }}
            onDeleteRequest={() => {
              setIsDeleteModalOpen(true);
            }}
            onRunPreview={
              isCoachingTemplate ? undefined : () => void onRunPreview()
            }
            isRunPreviewLoading={isPreviewRunStarting}
            // A preview runs the saved automation, not what the form shows.
            runPreviewDisabledReason={
              isCreditsExhausted
                ? aiCreditsDisabledReason
                : hasUnsavedConfig
                  ? "Save or cancel your changes before running a preview."
                  : undefined
            }
          />
        }
        tabs={
          isCoachingTemplate ? undefined : (
            <RouteTabs
              ariaLabel="Automation sections"
              tabs={[
                { label: "Overview", to: "." },
                { label: "Results", to: "results" },
                { label: "Runs", to: "runs" },
              ]}
            />
          )
        }
      />

      <PageBody fills>
        <AutomationTemplateDescription templateId={automation.template_id} />

        <Outlet
          context={
            {
              automation,
              automationsHref,
              reportHasUnsavedChanges: setHasUnsavedConfig,
            } satisfies AutomationDetailOutletContext
          }
        />
      </PageBody>

      <DeleteAutomationModal
        isOpen={isDeleteModalOpen}
        templateId={automation.template_id}
        onCancel={() => {
          setIsDeleteModalOpen(false);
        }}
        onDelete={() => {
          setIsDeleteModalOpen(false);
          void onDelete();
        }}
      />
    </PageLayout>
  );
};
